import {TableOperationDefinition} from "./table-operation-definition";

export class TableOperationModel {
  private readonly definition: TableOperationDefinition;

  _workflowOperation = false;
  _workflowActivityData = undefined;

  constructor(definition: TableOperationDefinition, workflowOperation: boolean = false, workflowActivityData?: any) {
    this.definition = definition;
    this._workflowOperation = workflowOperation;
    this._workflowActivityData = workflowActivityData;
    if (workflowOperation) {
      this.definition._showInDetailView = true;
      this.definition._showInListContextMenu = true;
      this.definition._isFormSubmitOperation = true;
    }
    if (workflowActivityData) {
      this.definition._id = this._workflowActivityData.transitionID;
      this.definition._onBefore = this._workflowActivityData.onBefore;
      this.definition._onSuccess = this._workflowActivityData.onSuccess;
      this.definition._executeConditionOnClient = this._workflowActivityData.executeOnClient === true;
      this.definition._condition = this._workflowActivityData.condition;
      this.definition._code = this._workflowActivityData.code;
    }
  }

  getTableName() {
    return this.getDefaultedField("_table");
  };

  getName() {
    return this.getDefaultedField("_name");
  };

  getI18nKey() {
    return this.getDefaultedField("_i18n");
  };

  getID() {
    return this.getDefaultedField("_id");
  };

  getCode() {
    return this.getDefaultedField("_code");
  };

  isCustomOperation() {
    return this.getDefaultedField("_isCustomOperation");
  };

  isBulkOperation() {
    return this.getDefaultedField("_isBulkOperation");
  };

  isFormSubmitOperation() {
    return this.getDefaultedField("_isFormSubmitOperation");
  };

  isClientOperation() {
    if (this.isWorkflowOperation()) {
      return false;
    } else {
      return this.getDefaultedField("_operationType") === "execute_on_client";
    }
  };

  isServerOperation() {
    if (this.isWorkflowOperation()) {
      return true;
    } else {
      return this.getDefaultedField("_operationType") === "execute_on_server";
    }
  };

  isActive() {
    return this.getDefaultedField("_isActive", true);
  };

  getCondition() {
    return this.getDefaultedField("_condition");
  };

  hasCondition() {
    return this.isNotBlank(this.getCondition());
  };

  executeConditionOnClient() {
    return this.getDefaultedField("_executeConditionOnClient", false);
  };

  getClientScript() {
    return this.getDefaultedField("_clientScript");
  };

  hasClientScript() {
    return this.isNotBlank(this.getClientScript());
  };

  getOnBeforeScript() {
    return this.getDefaultedField("_onBefore");
  };

  hasOnBeforeScript() {
    return this.isNotBlank(this.getOnBeforeScript());
  };

  getPrepareDataScript() {
    return this.getDefaultedField("_prepareData");
  };

  hasPrepareDataScript() {
    return this.isNotBlank(this.getPrepareDataScript());
  };

  getOnSuccessScript() {
    return this.getDefaultedField("_onSuccess");
  };

  hasOnSuccessScript() {
    return this.isNotBlank(this.getOnSuccessScript());
  };

  getOnFailureScript() {
    return this.getDefaultedField("_onFailure");
  };

  hasOnFailureScript() {
    return this.isNotBlank(this.getOnFailureScript());
  };

  getOrder() {
    return this.getDefaultedField("_order");
  };

  compareOrder(rhs) {
    const lhsOrder = (this.getOrder() !== undefined) ? this.getOrder() : 1000;
    const rhsOrder = (rhs.getOrder() !== undefined) ? rhs.getOrder() : 1000;
    return (lhsOrder - rhsOrder);
  };

  getRendererProperties() {
    return this.getDefaultedField("_rendererProperties", {});
  };

  getOperationType() {
    return this.getDefaultedField("_operationType");
  };

  showInListView() {
    return this.getDefaultedField("_showInListView", false);
  };

  showInDetailView() {
    return this.getDefaultedField("_showInDetailView", false);
  };

  showInListContextMenu() {
    return this.getDefaultedField("_showInListContextMenu", false);
  };

  showInDetailContextMenu() {
    return this.getDefaultedField("_showInDetailContextMenu", false);
  };

  showInDetailConfigMenu() {
    return this.getDefaultedField("_showInDetailConfigMenu", false);
  };

  showInListConfigMenu() {
    return this.getDefaultedField("_showInListConfigMenu", false);
  };

  getIcon() {
    return this.getDefaultedField("_icon");
  };

  separatorBefore() {
    return this.getDefaultedField("_separatorBefore", false);
  };

  separatorAfter() {
    return this.getDefaultedField("_separatorAfter", false);
  };

  isWorkflowOperation() {
    return this._workflowOperation;
  };

  getWorkflowActivityData() {
    return this._workflowActivityData;
  };

  private getDefaultedField(property: any, defaultValue?: any) {
    const value = this.definition[property];
    return value !== null && value !== undefined ? value : defaultValue;
  }

  private isNotBlank(s: string) {
    return s && s.trim().length > 0;
  }

  static create(definition: TableOperationDefinition): TableOperationModel {
    return new TableOperationModel(definition);
  }

  static createWorkflowActivityTableOperationModel(workflowActivityItem: any): TableOperationModel {
    return new TableOperationModel({}, true, workflowActivityItem.activityData);
  }

}
